import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

// MUI's `Snackbar`, ported as a shared Atlas component.
//
// The donor raises one on the item edit page for the result of a save
// (`product-edit-page.tsx:87`) and on other screens for their own results; the
// theme overrides nothing about `MuiSnackbar`, so **MUI's own defaults are the
// source spec**. Concretely:
//
//   - fixed, bottom centre by default, 24px up from the bottom edge — and below
//     the `sm` breakpoint it stops centring and pins itself 8px from each edge
//     instead. The other five of MUI's origins are `anchorOrigin`'s (below);
//   - it grows in over 225ms rather than appearing, and **shrinks back out over
//     195ms rather than vanishing** — `Grow` on the way in and on the way out,
//     each running opacity and transform on their own clocks (see the keyframes
//     in `index.css`);
//   - `autoHideDuration` closes it, and hovering it, focusing it, or leaving the
//     window **pauses** that clock — which is what lets someone read a long
//     message without racing it. Coming back does **not** restart the full
//     clock: MUI resumes at `autoHideDuration / 2` (`useSnackbar.js:71-75`), so
//     a message you have already looked at leaves sooner;
//   - **Escape closes it.** MUI reports that as `reason: 'escapeKeyDown'`, and
//     the donor's `useSnackbarState` returns early only on `'clickaway'`
//     (`useSnackbarState.ts:11-16`) — so a click elsewhere leaves it standing
//     and Escape does not.
//
// It renders the positioned box only. What is inside is the caller's, which for
// every donor call site so far is a `MuiAlert`.

/**
 * Where on the window the box sits: MUI's `anchorOrigin`.
 *
 * MUI's own default is bottom-*left* (`Snackbar.js:120-126`); every donor call
 * site Atlas ports names one, and bottom-centre is what all of them named
 * until the communities list's top-centre success toasts
 * (`CommunityListIsland.tsx:660-670`) — so bottom-centre is this port's
 * default, and no earlier caller moves.
 */
export interface MuiSnackbarOrigin {
  vertical: "top" | "bottom";
  horizontal: "left" | "center" | "right";
}

/**
 * MUI's `SnackbarRoot` rules, origin by origin (`Snackbar.js:46-105`). The
 * root is always `left: 8px; right: 8px` with its content centred; then:
 *
 *   - `top` / `bottom`: 8px off that edge, 24px from `sm` (600px) up;
 *   - `center`: nothing more below `sm`, and from `sm` up it stops spanning —
 *     `left: 50%; right: auto; transform: translateX(-50%)`;
 *   - `left` / `right`: justified to that end below `sm`, and from `sm` up 24px
 *     in from it with the other side let go.
 *
 * Literal class strings, because Tailwind scans source text and generates
 * nothing for a class assembled by interpolation.
 */
const VERTICAL_CLASSES = {
  top: "top-2 min-[600px]:top-6",
  bottom: "bottom-2 min-[600px]:bottom-6",
} as const;

const HORIZONTAL_CLASSES = {
  center: "min-[600px]:right-auto min-[600px]:left-1/2 min-[600px]:-translate-x-1/2",
  left: "min-[600px]:right-auto min-[600px]:left-6",
  right: "min-[600px]:right-6 min-[600px]:left-auto",
} as const;

/** The justification, on the full-width row inside the box — the box's one
 * child, so justifying the box itself would move nothing. */
const ROW_JUSTIFY = {
  center: "justify-center",
  left: "justify-start",
  right: "justify-end",
} as const;

export interface MuiSnackbarProps {
  open: boolean;
  /** Milliseconds before it closes itself. The donor's every call site passes
   * one; MUI's own default is not to auto-hide at all. */
  autoHideDuration?: number;
  onClose: () => void;
  /** The donor's edit page widens the box with `sx={{ minWidth: '52.5%' }}`;
   * without it MUI's content sits between 288 and 568px. */
  className?: string;
  /**
   * Close on a click anywhere outside the box — MUI's own `ClickAwayListener`
   * reason, which the item edit page's `useSnackbarState` filters out and the
   * Spec list's success snackbar does not (`SpecsListTableIsland.tsx:515-520`
   * closes on every reason). Off unless the call site's donor closes on it.
   */
  closeOnClickAway?: boolean;
  /**
   * Close on Escape — MUI's `escapeKeyDown` reason, which every donor call site
   * so far lets through. react-ui's `Notification` is the one that filters it
   * (`notification.tsx:62-64`), so the error snackbar turns it off.
   */
  closeOnEscape?: boolean;
  /**
   * Render into `document.body` — react-ui's `Snackbar` (`snackbar.tsx:32`),
   * and the Spec list's `<Portal>` around its own. Portalled, a snackbar raised
   * over an open modal dialog sits outside the tree the dialog hides and makes
   * inert, so it still pauses under the pointer and is still announced, as the
   * donor's is over MUI's modal.
   */
  portal?: boolean;
  /**
   * Leave at once rather than shrink out — react-ui's `Snackbar` returns
   * `null` the moment it is closed (`snackbar.tsx:25-27`), so the donor's
   * `Notification` has no exit transition.
   */
  instantExit?: boolean;
  /** MUI's `anchorOrigin`; bottom-centre when omitted — see the type. */
  anchorOrigin?: MuiSnackbarOrigin;
  /**
   * react-ui's `anchorOffset` (`notification.tsx:10-22,85-88`): CSS lengths
   * that win over the origin's own offset at every width — the donor applies
   * them as `!important` styles over MUI's breakpoint rules, and an inline
   * style here outranks the origin's classes the same way. The link-delete
   * toast's is `{ top: 'calc(var(--app-header-height, 0px) + 24px)' }`: the
   * donor reads the app header's height off that same property
   * (`useAppHeaderHeight.ts`) and adds 24.
   */
  anchorOffset?: Partial<Record<"top" | "bottom" | "left" | "right", string>>;
  children: ReactNode;
}

const DEFAULT_ORIGIN: MuiSnackbarOrigin = { vertical: "bottom", horizontal: "center" };

export function MuiSnackbar({
  open,
  autoHideDuration,
  onClose,
  className,
  closeOnClickAway = false,
  closeOnEscape = true,
  portal = false,
  instantExit = false,
  anchorOrigin = DEFAULT_ORIGIN,
  anchorOffset,
  children,
}: MuiSnackbarProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  /**
   * Whether the box is in the DOM at all — which outlives `open` by the length
   * of the exit.
   *
   * MUI keeps the node mounted through `Grow`'s leave transition and drops it on
   * `onExited`. Returning `null` the moment `open` goes false made the message
   * vanish instantly, which is the one part of the transition anybody actually
   * has time to watch.
   */
  const [shown, setShown] = useState(open);
  /** `open` as of the last render, so an opening can be caught during render
   * rather than in an effect that would paint one frame without the box. */
  const [wasOpen, setWasOpen] = useState(open);
  const [paused, setPaused] = useState(false);
  /**
   * Whether the clock has already been paused once.
   *
   * MUI's `handleResume` restarts at `autoHideDuration / 2` rather than at the
   * full duration — the reader has had their look, and the message should not
   * outstay a fresh one because they swept the pointer over it.
   */
  const [resumed, setResumed] = useState(false);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setShown(true);
      // Each opening gets the whole clock: MUI's pause and resume act on the
      // one showing's timer (`useSnackbar.js:55-75`) and leave nothing behind.
      setPaused(false);
      setResumed(false);
    }
  }
  /** `autoHideDuration` as of the last render. MUI's timer effect re-runs on
   * it too (`useSnackbar.js:55-60`) and starts the **whole** new duration —
   * after a resume, and even with the pointer still on the box, since a hover
   * only cleared the timer it found. A host that keeps one snackbar mounted
   * and changes its duration (the communities list's one notification slot,
   * #752) gets the same. */
  const [wasDuration, setWasDuration] = useState(autoHideDuration);
  if (autoHideDuration !== wasDuration) {
    setWasDuration(autoHideDuration);
    if (open) {
      setPaused(false);
      setResumed(false);
    }
  }

  // The latest `onClose`, read when the timer fires rather than listed as the
  // timer's dependency: MUI's timer effect hangs on stable callbacks, so a host
  // re-rendering with a fresh inline handler never winds the clock back.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const pause = () => setPaused(true);
  const resume = () => {
    setPaused(false);
    setResumed(true);
  };

  useEffect(() => {
    if (!open || autoHideDuration === undefined || paused) return;
    const timer = setTimeout(
      () => onCloseRef.current(),
      resumed ? autoHideDuration / 2 : autoHideDuration,
    );
    return () => clearTimeout(timer);
  }, [open, autoHideDuration, paused, resumed]);

  // MUI's window-blur listener, which it enables by default
  // (`disableWindowBlurListener = false`): switching away from the tab holds the
  // message rather than spending it on an empty screen.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("blur", pause);
    window.addEventListener("focus", resume);
    return () => {
      window.removeEventListener("blur", pause);
      window.removeEventListener("focus", resume);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // An Escape another layer answered is that layer's
      // (`useSnackbar.js:31-36`): MUI's modals swallow theirs, and Radix's
      // layers — and the select port — prevent the ones they close on.
      if (event.key === "Escape" && !event.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeOnEscape, onClose]);

  // MUI's `ClickAwayListener`: a `click` (and a `touchend`) on the document
  // whose target is outside the box. It arms a tick after it mounts
  // (`ClickAwayListener.js:54-58`) — React may commit the box, and attach this,
  // while the click that raised it is still bubbling — and it lets a touch that
  // moved, a scroll, go by (`:83-85,116-126`). A click whose propagation was
  // stopped never reaches MUI's listener, as modsy's React root is `#root`;
  // here React itself listens on the document, where a stop cannot hold back
  // a second listener on the same node, so the stop is read off the event.
  useEffect(() => {
    if (!open || !closeOnClickAway) return;
    let armed = false;
    let moved = false;
    const arm = setTimeout(() => {
      armed = true;
    });
    const onMove = () => {
      moved = true;
    };
    const onAway = (event: Event) => {
      if (!armed || event.cancelBubble) return;
      if (moved) {
        moved = false;
        return;
      }
      if (event.target instanceof Node && boxRef.current?.contains(event.target)) return;
      onClose();
    };
    document.addEventListener("click", onAway);
    document.addEventListener("touchend", onAway);
    document.addEventListener("touchmove", onMove);
    return () => {
      clearTimeout(arm);
      document.removeEventListener("click", onAway);
      document.removeEventListener("touchend", onAway);
      document.removeEventListener("touchmove", onMove);
    };
  }, [open, closeOnClickAway, onClose]);

  // `leavingScreen`, the exit half of `Snackbar`'s default `transitionDuration`.
  useEffect(() => {
    if (open || !shown) return;
    const timer = setTimeout(() => setShown(false), 195);
    return () => clearTimeout(timer);
  }, [open, shown]);

  if (!shown || (instantExit && !open)) return null;

  const box = (
    <div
      ref={boxRef}
      data-slot="mui-snackbar"
      data-state={open ? "open" : "closed"}
      // No role of its own: the live region is the `MuiAlert` inside, which
      // renders `role="alert"` exactly as MUI's does, so this box stays out of
      // the accessibility tree rather than announcing the message twice.
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      className={cn(
        // `pointer-events-auto` against a modal dialog's `pointer-events: none`
        // on the body, so the pointer still reaches it (and pauses its clock).
        "pointer-events-auto fixed right-2 left-2 z-[1400] flex items-center",
        VERTICAL_CLASSES[anchorOrigin.vertical],
        HORIZONTAL_CLASSES[anchorOrigin.horizontal],
        className,
      )}
      style={anchorOffset}
    >
      {/* The grow lives on its own element: the box above centres itself with a
        `translateX(-50%)`, and an animation that keyframes `transform` would
        overwrite that and drop the snackbar half a width to the right. */}
      {/* A centring row, as MUI's root is (`justifyContent: center`): an alert
        with no width of its own is as wide as its message; one at `w-full`,
        as the donor's snackbars mostly ask, fills it. */}
      <div
        className={cn(
          "flex w-full",
          ROW_JUSTIFY[anchorOrigin.horizontal],
          open ? "animate-mui-grow-in" : "animate-mui-grow-out",
        )}
      >
        {children}
      </div>
    </div>
  );

  return portal && typeof document !== "undefined" ? createPortal(box, document.body) : box;
}
